/**
 * Starter icon set — Phosphor Icons, the canonical library for the design
 * system. Exposed through a thin adapter so app code keeps the familiar `size=`
 * API and stable names:
 *   import { Search, Check, Phone } from '~/components/icons';
 *   <Search size={20} />
 *
 * Phosphor ships six weights (thin / light / regular / bold / fill / duotone)
 * selectable per icon via a `weight` prop. The kit default is **duotone** — a
 * soft filled layer behind the line that reads premium against the teal/brass
 * palette without going heavy. Override per use: `<Home size={18} weight="bold" />`.
 *
 * Icons inherit `currentColor` (Phosphor's default `fill`). They are decorative
 * by default — pass `aria-hidden` for purely ornamental marks, or for a
 * meaningful standalone icon pass `aria-hidden={false}` + `aria-label`.
 *
 * For filled active/selected affordances, set `weight="fill"` per use.
 * Brand marks (Google / Gmail) are inline SVGs — icon libraries don't ship them.
 */
import {
  forwardRef,
  type ComponentType,
  type ReactElement,
  type SVGProps,
} from 'react';
import {
  ArrowLeft as ArrowLeftGlyph,
  ArrowRight as ArrowRightGlyph,
  ArrowsClockwise,
  Bank as BankGlyph,
  Buildings,
  CalendarBlank,
  CaretDown,
  CaretUp,
  Certificate as CertificateGlyph,
  ChartLineUp as ChartLineUpGlyph,
  Check as CheckGlyph,
  CircleNotch,
  Cloud as CloudGlyph,
  HandPointing as HandPointingGlyph,
  LockSimple as LockGlyph,
  Copy as CopyGlyph,
  CreditCard as CreditCardGlyph,
  DownloadSimple,
  Envelope,
  Faders,
  FileText as FileTextGlyph,
  Flag as FlagGlyph,
  Funnel,
  Gear,
  Heart as HeartGlyph,
  House,
  Info as InfoGlyph,
  ListChecks as ListChecksGlyph,
  MagnifyingGlass,
  Moon as MoonGlyph,
  Notebook,
  PaperPlaneTilt,
  PencilSimple,
  Phone as PhoneGlyph,
  Plus as PlusGlyph,
  Receipt as ReceiptGlyph,
  Robot,
  ShieldCheck as ShieldCheckGlyph,
  ShieldWarning as ShieldWarningGlyph,
  SignOut,
  Sun as SunGlyph,
  Tray,
  Trash,
  UploadSimple,
  User as UserGlyph,
  Warning,
  WarningCircle,
  X as XGlyph,
  type Icon as PhosphorIconComponent,
  type IconProps as PhosphorIconProps,
  type IconWeight,
} from '@phosphor-icons/react';

/**
 * Props for every kit icon — Phosphor's icon props (lucide-compatible `size`,
 * `weight`, plus standard SVG attributes) with the ref handled by the wrapper.
 * Based on Phosphor's own props so `color` etc. line up under
 * exactOptionalPropertyTypes.
 */
export interface IconProps extends Omit<PhosphorIconProps, 'ref'> {
  /** Pixel size — sets both width and height (default 24). */
  readonly size?: number | string;
  /** Phosphor weight. Defaults to the kit default (duotone; `fill` for *Solid). */
  readonly weight?: IconWeight;
  /** Accepted for source-compatibility with the previous lucide API (no-op). */
  readonly absoluteStrokeWidth?: boolean;
}

export type IconComponent = ComponentType<IconProps>;

// Back-compat type aliases — these names predate the icon-library swaps and are
// still referenced by the icon-gallery stories.
export type LucideProps = IconProps;
export type LucideIcon = IconComponent;

/**
 * Wrap a Phosphor icon so it accepts our `size` API, defaults the `weight`, and
 * forwards refs. Phosphor already uses a `size` prop (width + height), so this
 * mostly pins the kit's default weight and keeps the prop surface stable.
 */
function icon(
  Glyph: PhosphorIconComponent,
  displayName: string,
  defaultWeight: IconWeight = 'light',
): IconComponent {
  const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
    { size = 24, weight, absoluteStrokeWidth: _absoluteStrokeWidth, ...rest },
    ref,
  ): ReactElement {
    return <Glyph ref={ref} size={size} weight={weight ?? defaultWeight} {...rest} />;
  });
  Icon.displayName = displayName;
  return Icon;
}

export const AlertCircle = icon(WarningCircle, 'AlertCircle');
export const AlertTriangle = icon(Warning, 'AlertTriangle');
export const ArrowLeft = icon(ArrowLeftGlyph, 'ArrowLeft');
export const ArrowRight = icon(ArrowRightGlyph, 'ArrowRight');
export const Bank = icon(BankGlyph, 'Bank');
export const Bot = icon(Robot, 'Bot');
export const Building2 = icon(Buildings, 'Building2');
export const Calendar = icon(CalendarBlank, 'Calendar');
// File-type marks for the uploader's three groups: the loan portfolio over time
// (ChartLineUp), the original loan terms (Certificate), and individual ledger
// transactions (Receipt).
export const Certificate = icon(CertificateGlyph, 'Certificate');
export const ChartLineUp = icon(ChartLineUpGlyph, 'ChartLineUp');
export const Receipt = icon(ReceiptGlyph, 'Receipt');
export const Check = icon(CheckGlyph, 'Check');
export const ChevronDown = icon(CaretDown, 'ChevronDown');
export const ChevronUp = icon(CaretUp, 'ChevronUp');
export const Copy = icon(CopyGlyph, 'Copy');
export const CreditCard = icon(CreditCardGlyph, 'CreditCard');
export const Download = icon(DownloadSimple, 'Download');
export const FileText = icon(FileTextGlyph, 'FileText');
export const Flag = icon(FlagGlyph, 'Flag');
export const Heart = icon(HeartGlyph, 'Heart');
export const Home = icon(House, 'Home');
export const Cloud = icon(CloudGlyph, 'Cloud');
export const Lock = icon(LockGlyph, 'Lock');
export const HandPointing = icon(HandPointingGlyph, 'HandPointing');
export const Inbox = icon(Tray, 'Inbox');
export const Info = icon(InfoGlyph, 'Info');
export const ListChecks = icon(ListChecksGlyph, 'ListChecks');
export const ListFilter = icon(Funnel, 'ListFilter');
// Spinner — bold (single-weight) reads cleaner than duotone while rotating.
export const Loader2 = icon(CircleNotch, 'Loader2', 'bold');
export const LogOut = icon(SignOut, 'LogOut');
export const Mail = icon(Envelope, 'Mail');
export const Moon = icon(MoonGlyph, 'Moon');
export const NotebookText = icon(Notebook, 'NotebookText');
export const Pencil = icon(PencilSimple, 'Pencil');
export const Phone = icon(PhoneGlyph, 'Phone');
export const Plus = icon(PlusGlyph, 'Plus');
export const RefreshCcw = icon(ArrowsClockwise, 'RefreshCcw');
export const Search = icon(MagnifyingGlass, 'Search');
export const Send = icon(PaperPlaneTilt, 'Send');
export const Settings = icon(Gear, 'Settings');
export const ShieldCheck = icon(ShieldCheckGlyph, 'ShieldCheck');
export const ShieldWarning = icon(ShieldWarningGlyph, 'ShieldWarning');
// The Segment Builder's date "Slider" view toggle (vs the "Calendar" popover).
export const SlidersHorizontal = icon(Faders, 'SlidersHorizontal');
export const Sun = icon(SunGlyph, 'Sun');
export const Trash2 = icon(Trash, 'Trash2');
export const Upload = icon(UploadSimple, 'Upload');
export const User = icon(UserGlyph, 'User');
export const X = icon(XGlyph, 'X');

/** Render props for the inline brand-mark components below. */
type BrandIconProps = SVGProps<SVGSVGElement> & {
  /** Pixel size — sets both width and height. */
  readonly size?: number;
};

/**
 * Google "G" wordmark in its canonical brand colours. Icon libraries don't
 * ship brand marks; this stays here so "Sign in with Google" buttons keep
 * their recognisable signal.
 */
export function GoogleBrand({
  size = 16,
  ...rest
}: BrandIconProps): ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...rest}
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path
        fill="#FBBC05"
        d="M5.85 14.1A6.56 6.56 0 0 1 5.5 12c0-.73.13-1.44.35-2.1V7.07H2.18a11 11 0 0 0 0 9.87l3.67-2.84Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07L5.85 9.9C6.71 7.31 9.14 5.38 12 5.38Z"
      />
    </svg>
  );
}

/**
 * Gmail envelope mark in its canonical colour. Use it on buttons that
 * connect or reference a Gmail account.
 */
export function GmailBrand({
  size = 16,
  ...rest
}: BrandIconProps): ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...rest}
    >
      <path
        fill="#4285F4"
        d="M2 7.13v10.62A2.25 2.25 0 0 0 4.25 20H6V11L2 7.13Z"
      />
      <path
        fill="#34A853"
        d="M18 20h1.75A2.25 2.25 0 0 0 22 17.75V7.13L18 11v9Z"
      />
      <path
        fill="#FBBC04"
        d="M6 11v9h12v-9l-6 4.5L6 11Z"
      />
      <path
        fill="#EA4335"
        d="M6 11l6 4.5L18 11V5.5L12 10 6 5.5V11Z"
      />
      <path
        fill="#C5221F"
        d="M2 7.13 6 11V5.5L4.21 4.13A2.25 2.25 0 0 0 2 6.38v.75Z"
      />
      <path
        fill="#C5221F"
        d="M22 7.13v-.75a2.25 2.25 0 0 0-2.21-2.25L18 5.5V11l4-3.87Z"
      />
    </svg>
  );
}
